import axios, {
  AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { getAccessToken } from '../auth/authTokenProvider';
import { reportApiError } from '../utils/errorReporting';
import {
  getTelemetryDurationMs,
  getTelemetryStartTime,
  reportApiMetric,
} from '../utils/apiTelemetry';

/** RM Resource Matrix API base — explicit var preferred, falls back to legacy gateway env. */
export const getRmApiBaseUrl = (): string =>
  import.meta.env.VITE_RM_API_URL?.trim() ||
  import.meta.env.VITE_AUTH_API_ORIGIN?.trim() ||
  '';

/** TCM call-deflection / workflow API base. */
export const getTcmApiBaseUrl = (): string =>
  import.meta.env.VITE_TCM_API_URL?.trim() ||
  import.meta.env.VITE_BACKEND_URL?.trim() ||
  'https://tcm-gateway-6pp1kl82.wl.gateway.dev';

let last401Time = 0;
const REDIRECT_DEBOUNCE_MS = 5000;

type TelemetryAxiosConfig = InternalAxiosRequestConfig & {
  telemetryStartTime?: number;
};

const getAxiosTelemetryUrl = (
  config: TelemetryAxiosConfig | undefined
): string | undefined => {
  if (!config?.url) {
    return undefined;
  }

  if (!config.baseURL) {
    return config.url;
  }

  try {
    return new URL(config.url, config.baseURL).toString();
  } catch {
    return config.url;
  }
};

const reportAxiosMetric = (
  config: TelemetryAxiosConfig | undefined,
  response: AxiosResponse | undefined,
  success: boolean
): void => {
  reportApiMetric({
    method: config?.method,
    url: getAxiosTelemetryUrl(config),
    status: response?.status,
    durationMs: getTelemetryDurationMs(config?.telemetryStartTime),
    success,
  });
};

const handle401 = (client: AxiosInstance): void => {
  const now = Date.now();
  if (now - last401Time < REDIRECT_DEBOUNCE_MS) {
    return;
  }
  last401Time = now;

  console.warn('[Auth] 401 Unauthorized - suppressing auto-reload redirect');

  delete client.defaults.headers.common['Authorization'];

  // Do not force navigation/reload from the network layer.
  // Repeated 401 responses can otherwise create a reload loop that blocks the UI.
};

/**
 * Axios instance with the same auth + 401 behavior as the legacy RM client.
 * Use for RM Resource Matrix APIs (default gateway).
 */
export const createAuthenticatedAxios = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
  });

  client.interceptors.request.use(
    async (config) => {
      (config as TelemetryAxiosConfig).telemetryStartTime =
        getTelemetryStartTime();

      const token = await getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => {
      reportAxiosMetric(response.config as TelemetryAxiosConfig, response, true);
      return response;
    },
    (error) => {
      reportApiError(error);
      reportAxiosMetric(
        error.config as TelemetryAxiosConfig | undefined,
        error.response,
        false
      );

      if (
        error.response?.status === 401 &&
        !error.config?.url?.includes('/auth/') &&
        !error.config?.url?.includes('/login')
      ) {
        handle401(client);
      }
      return Promise.reject(error);
    }
  );

  return client;
};
